import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";

import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TablePagination from "@material-ui/core/TablePagination";
import Chip from "@material-ui/core/Chip";
import Box from "@material-ui/core/Box";
import CircularProgress from "@material-ui/core/CircularProgress";
import TextField from "@material-ui/core/TextField";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";

const basePath =
  process.env.NODE_ENV === "production" ? "/edge" : "http://localhost:3010";

const STATUS_COLORS = {
  open: "#2196f3",
  in_progress: "#ff9800",
  waiting_on_player: "#9c27b0",
  resolved: "#4caf50",
  closed: "#757575",
};

const PRIORITY_COLORS = {
  normal: "#757575",
  high: "#f44336",
};

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: "#1C1E20",
    minHeight: "100vh",
  },
  title: {
    fontSize: "1.8rem",
    fontWeight: 700,
    color: "#ffffff",
    marginBottom: theme.spacing(3),
  },
  filters: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  tableContainer: {
    backgroundColor: "#262829",
    borderRadius: 12,
  },
  tableHead: {
    "& th": {
      fontWeight: 700,
      color: "rgba(255,255,255,0.7)",
      borderBottom: "1px solid rgba(255,255,255,0.1)",
    },
  },
  row: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.03)",
    },
    "& td": {
      color: "rgba(255,255,255,0.85)",
      borderBottom: "1px solid rgba(255,255,255,0.06)",
    },
  },
}));

export default function SupportTicketsPage({ catalog }) {
  const classes = useStyles();
  const history = useHistory();
  const user = catalog?.user;

  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [search, setSearch] = useState("");

  const fetchTickets = useCallback(async () => {
    if (!user?.userId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      if (categoryFilter) params.set("category", categoryFilter);
      if (search) params.set("search", search);
      params.set("limit", rowsPerPage);
      params.set("skip", page * rowsPerPage);

      const url = `${basePath}/api/v7/admin/support-tickets/adpass/${user.userId}?${params}`;
      const resp = await fetch(url, { credentials: "include" });
      const data = await resp.json();
      if (data.ok) {
        setTickets(data.tickets || []);
        setTotal(data.total || 0);
      }
    } catch (err) {
      console.error("Failed to fetch support tickets", err);
    } finally {
      setLoading(false);
    }
  }, [user, page, rowsPerPage, statusFilter, categoryFilter, search]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const formatDate = (d) => {
    if (!d) return "-";
    return new Date(d).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className={classes.root}>
      <Typography className={classes.title}>Support Tickets</Typography>

      <Box className={classes.filters}>
        <TextField
          label="Search"
          variant="outlined"
          size="small"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          style={{ minWidth: 220 }}
          InputProps={{ style: { color: "#fff" } }}
          InputLabelProps={{ style: { color: "rgba(255,255,255,0.5)" } }}
        />
        <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
          <InputLabel style={{ color: "rgba(255,255,255,0.5)" }}>Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            label="Status"
            style={{ color: "#fff" }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="open">Open</MenuItem>
            <MenuItem value="in_progress">In Progress</MenuItem>
            <MenuItem value="waiting_on_player">Waiting on Player</MenuItem>
            <MenuItem value="resolved">Resolved</MenuItem>
            <MenuItem value="closed">Closed</MenuItem>
          </Select>
        </FormControl>
        <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
          <InputLabel style={{ color: "rgba(255,255,255,0.5)" }}>Category</InputLabel>
          <Select
            value={categoryFilter}
            onChange={(e) => { setCategoryFilter(e.target.value); setPage(0); }}
            label="Category"
            style={{ color: "#fff" }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="account">Account</MenuItem>
            <MenuItem value="billing">Billing</MenuItem>
            <MenuItem value="matchmaking">Matchmaking</MenuItem>
            <MenuItem value="report">Report</MenuItem>
            <MenuItem value="bug">Bug</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress style={{ color: "#FF7500" }} />
        </Box>
      ) : (
        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table>
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell>Ticket</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Submitter</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Updated</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tickets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" style={{ color: "rgba(255,255,255,0.5)" }}>
                    No tickets found.
                  </TableCell>
                </TableRow>
              ) : (
                tickets.map((t) => (
                  <TableRow
                    key={t._id}
                    className={classes.row}
                    onClick={() => history.push(`/admin/support-tickets/${t._id}`)}
                  >
                    <TableCell>{t.ticketNumber}</TableCell>
                    <TableCell style={{ textTransform: "capitalize" }}>{t.category}</TableCell>
                    <TableCell>{t.subject}</TableCell>
                    <TableCell>{t.nickname || t.email}</TableCell>
                    <TableCell>
                      <Chip
                        label={t.status?.replace(/_/g, " ")}
                        size="small"
                        style={{
                          backgroundColor: STATUS_COLORS[t.status] || "#757575",
                          color: "#fff",
                          fontWeight: 600,
                          textTransform: "capitalize",
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={t.priority}
                        size="small"
                        variant="outlined"
                        style={{
                          borderColor: PRIORITY_COLORS[t.priority] || "#757575",
                          color: PRIORITY_COLORS[t.priority] || "#757575",
                          fontWeight: 600,
                          textTransform: "capitalize",
                        }}
                      />
                    </TableCell>
                    <TableCell>{formatDate(t.createdAt)}</TableCell>
                    <TableCell>{formatDate(t.updatedAt)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            style={{ color: "rgba(255,255,255,0.7)" }}
          />
        </TableContainer>
      )}
    </div>
  );
}

SupportTicketsPage.propTypes = {
  catalog: PropTypes.object.isRequired,
};
